import {
  Conversation,
  ConversationParticipant,
} from '@stripe-internal/conversations-client';
import {useState, useEffect} from 'react';
import {filter, Subscription} from 'rxjs';
import {TypingRowDotCom} from '../Chat';

type Props = {
  conversation: Conversation;
};

function useConversationTypers(conversation: Conversation) {
  const [typers, setTypers] = useState<ConversationParticipant[]>([]);

  useEffect(() => {
    const sub = new Subscription();
    sub.add(conversation.typingObservable().subscribe(setTypers));
    return () => sub.unsubscribe();
  }, [conversation]);

  return typers;
}

export function DotComLoadingIndicator({conversation}: Props) {
  const [botTyping, setBotTyping] = useState<boolean>(
    !!conversation.context.placeholderConversation,
  );
  const [agentTyping, setAgentTyping] = useState(false);
  const typers = useConversationTypers(conversation);

  const botTyper = typers.some(
    (typer) => typer.id !== conversation.user.id && typer.role === 'bot',
  );

  const agentTyper = typers.some(
    (typer) => typer.id !== conversation.user.id && typer.role === 'agent',
  );

  // Shows the indicator when a bot or agent starts typing.
  // Prevents UI flicker: There is a small delay between the 'typing stopped' event arriving and the message being rendered.
  // To bridge this gap, the indicator is hidden only when:
  // 1. A new message event (`conversation-message-added`) is received.
  // 2. An agent stops typing and a short delay passes without a new message.
  useEffect(() => {
    if (botTyper) {
      setBotTyping(true);
    }

    if (agentTyper) {
      setAgentTyping(true);
    }
  }, [botTyper, agentTyper]);

  // For inline-message type the loading indicator within ResponseContent will take over from here.
  // See: @/frontend/mse-inline-data/src/UniversalHelpLiveAnswersResponse/ResponseContent.tsx
  useEffect(() => {
    const sub = new Subscription();
    sub.add(
      conversation
        .eventsObservable()
        .pipe(filter((e) => e.name === 'conversation-message-added'))
        .subscribe(() => {
          setBotTyping(false);
          setAgentTyping(false);
        }),
    );
    return () => sub.unsubscribe();
  }, [conversation]);

  // An agent could stop typing without sending a message, and we don't want to show the loading forever
  // In case they sent a message, to avoid flickering, we wait a bit before hiding the loading indicator
  useEffect(() => {
    const NO_MESSAGE_LOADED_DELAY = 500;
    const timeoutId = setTimeout(() => {
      if (agentTyping && !agentTyper) {
        setBotTyping(false);
        setAgentTyping(false);
      }
    }, NO_MESSAGE_LOADED_DELAY);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [agentTyping, agentTyper]);

  return botTyping || agentTyping ? <TypingRowDotCom /> : null;
}
